import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  getISOWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  differenceInCalendarDays,
} from "date-fns";
import { currentDateFnsLocale } from "../../../../i18n/dateLocales";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { ProjectTitle } from "../../../Utils";
import { hourHasProjectActivity, projectActivityLine } from "../projectActivityDisplay";
import { ProjectSelector } from "../../../utils/moduleSelectors/projectSelector";
import { ModernProjectActivitySelectorField } from "../../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { ApproveHourDialog } from "../approveHourDialog";
import { PMDialog } from "../../../utils/PMDialog";
import { AgreedOvertimeBadge } from "../AgreedOvertimeBadge";

const PROJECT_COLORS = ["#2E5BFF", "#0F9E8E", "#E8A33D", "#B4531F", "#7C3AED", "#0891B2"];

const projectColor = (projectId) => {
  if (projectId == null) return PROJECT_COLORS[0];
  const n = Number(projectId);
  if (Number.isNaN(n)) return PROJECT_COLORS[0];
  return PROJECT_COLORS[Math.abs(n) % PROJECT_COLORS.length];
};

const hourIsSynced = (hour) => !!(hour?.tripletex_entry_id && hour?.last_synced_at);

const sumHoursList = (list) => {
  if (!Array.isArray(list)) return 0;
  return list.reduce((s, h) => s + (parseFloat(h?.time) || 0), 0);
};

const hoursInDateRange = (list, fromStr, toStr) => {
  if (!Array.isArray(list) || !fromStr || !toStr) return 0;
  const from = fromStr.substring(0, 10);
  const to = toStr.substring(0, 10);
  return sumHoursList(
    list.filter((h) => {
      const d = (h?.date || "").substring(0, 10);
      return d && d >= from && d <= to;
    })
  );
};

const normalHoursBetween = (dateFromStr, dateToStr, scaledWeekdays) => {
  if (!scaledWeekdays || !dateFromStr || !dateToStr) return null;
  const start = parseISO(dateFromStr.substring(0, 10));
  const end = parseISO(dateToStr.substring(0, 10));
  if (start > end) return null;
  const map = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  let sum = 0;
  eachDayOfInterval({ start, end }).forEach((day) => {
    const key = map[day.getDay()];
    const v = parseFloat(scaledWeekdays[key] ?? 0);
    if (!Number.isNaN(v)) sum += v;
  });
  return sum;
};

function entryTimeLabel(hour) {
  if (hour?.start_time && hour?.end_time) {
    return `${String(hour.start_time).slice(0, 5)} – ${String(hour.end_time).slice(0, 5)}`;
  }
  if (hour?.created_at) {
    try {
      return format(new Date(hour.created_at), "HH:mm", { locale: currentDateFnsLocale() });
    } catch {
      return "";
    }
  }
  return "";
}

function EntryCard({ hour, onHourClick, onProcess, canProcess, showSyncStatus, t }) {
  const color = projectColor(hour?.project?.id || hour?.project_id);
  const synced = hourIsSynced(hour);
  const activityLabel = hourHasProjectActivity(hour)
    ? projectActivityLine(hour.project_activity) || t("project_activity.missing")
    : null;
  return (
    <div
      className="hts-entry"
      role="button"
      tabIndex={0}
      onClick={() => onHourClick?.(hour.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onHourClick?.(hour.id);
      }}
    >
      <div className="hts-entry-dot" style={{ background: color }} />
      <div>
        <div className="hts-entry-hours">{(parseFloat(hour.time) || 0).toFixed(1)}t</div>
        <div className="hts-entry-time">{entryTimeLabel(hour)}</div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="hts-entry-project">
          {hour.project?.id ? <ProjectTitle project={hour.project} /> : t("No project")}
        </div>
        {(activityLabel || showSyncStatus || hour?.agreed_overtime_requested || hour?.agreed_overtime_confirmed) && (
          <div className="hts-entry-meta">
            {activityLabel && <span>{activityLabel}</span>}
            {activityLabel && showSyncStatus && (
              <span style={{ color: "#D6D2C9" }}>·</span>
            )}
            {showSyncStatus && (
              <span className={`hts-status ${synced ? "hts-status-ok" : "hts-status-warn"}`}>
                {synced ? t("Synkronisert") : t("Ikke synkronisert")}
              </span>
            )}
            <AgreedOvertimeBadge hour={hour} />
          </div>
        )}
      </div>
      <div className="hts-entry-actions">
        <button
          type="button"
          className="hts-btn-edit"
          onClick={(e) => {
            e.stopPropagation();
            onHourClick?.(hour.id);
          }}
        >
          {t("Edit")}
        </button>
        {canProcess && (
          <button
            type="button"
            className="hts-btn-process"
            onClick={(e) => {
              e.stopPropagation();
              onProcess?.(hour);
            }}
          >
            {t("Process")}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Uke-strip Timeliste body (design zip layout).
 * Receives data/callbacks from HoursViewComponent — does not fetch.
 */
export function HoursUkeStripBody({
  controller,
  hoursData,
  statsExtraHours,
  scaledWeekdays,
  isLoading,
  statsLoading,
  today,
  setPeriodType,
  showFilters,
  showFiltersMenu,
  setShowFiltersMenu,
  selectedProject,
  setSelectedProject,
  selectedProjectActivity,
  setSelectedProjectActivity,
  activeFiltersCount,
  clearFilters,
  navigateDate,
  goToToday,
  updateController,
  getDateRangeForView,
  onHourClick,
  onNewHourClick,
  canProcessHours,
  selectedHour,
  setSelectedHour,
  processDialogOpen,
  setProcessDialogOpen,
  handleHourUpdated,
  infoBannerHidden,
  setInfoBannerHidden,
  statsDialogOpen,
  setStatsDialogOpen,
}) {
  const { t } = useTranslation();

  const hoursSource = useMemo(() => {
    if (Array.isArray(statsExtraHours) && statsExtraHours.length) return statsExtraHours;
    return hoursData || [];
  }, [statsExtraHours, hoursData]);

  const weekAnchor = useMemo(
    () => parseISO((controller.date || format(today, "yyyy-MM-dd")).substring(0, 10)),
    [controller.date, today]
  );
  const weekStart = useMemo(
    () => startOfWeek(weekAnchor, { weekStartsOn: 1 }),
    [weekAnchor]
  );
  const weekEnd = useMemo(
    () => endOfWeek(weekAnchor, { weekStartsOn: 1 }),
    [weekAnchor]
  );
  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd]
  );
  const weekNum = getISOWeek(weekStart);

  const dayTotals = useMemo(() => {
    return weekDays.map((day) => {
      const key = format(day, "yyyy-MM-dd");
      const h = hoursInDateRange(hoursSource, key, key);
      return { day, key, hours: h };
    });
  }, [weekDays, hoursSource]);

  const maxDay = Math.max(7.5, ...dayTotals.map((d) => d.hours), 0.1);
  const selectedKey = (controller.date || "").substring(0, 10);

  const pickDay = (day) => {
    const d = format(day, "yyyy-MM-dd");
    const view = controller.view === "period" ? "day" : controller.view;
    const { dateFrom, dateTo } = getDateRangeForView(d, view);
    updateController({
      ...controller,
      view,
      date: d,
      date_from: dateFrom,
      date_to: dateTo,
    });
  };

  const unsynced = useMemo(
    () => (hoursData || []).filter((h) => !hourIsSynced(h)),
    [hoursData]
  );

  const currWeekStart = format(weekStart, "yyyy-MM-dd");
  const currWeekEnd = format(weekEnd, "yyyy-MM-dd");
  const workedThisWeek = hoursInDateRange(hoursSource, currWeekStart, currWeekEnd);
  const normalThisWeek = normalHoursBetween(currWeekStart, currWeekEnd, scaledWeekdays);
  const pctThisWeek =
    normalThisWeek != null && normalThisWeek > 0
      ? Math.min(999, Math.round((workedThisWeek / normalThisWeek) * 1000) / 10)
      : null;
  const leftThisWeek =
    normalThisWeek != null ? Math.max(0, normalThisWeek - workedThisWeek) : null;

  const prevWeekStart = format(
    startOfWeek(new Date(weekStart.getTime() - 7 * 86400000), { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );
  const prevWeekEnd = format(
    endOfWeek(new Date(weekStart.getTime() - 7 * 86400000), { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );
  const workedPrevWeek = hoursInDateRange(hoursSource, prevWeekStart, prevWeekEnd);

  const mFrom = format(startOfMonth(weekAnchor), "yyyy-MM-dd");
  const mTo = format(endOfMonth(weekAnchor), "yyyy-MM-dd");
  const workedMonth = hoursInDateRange(hoursSource, mFrom, mTo);
  const workedPeriod = sumHoursList(hoursData);

  const projectSplit = useMemo(() => {
    const map = new Map();
    (hoursData || []).forEach((h) => {
      const id = h.project?.id || h.project_id || "none";
      const name = h.project?.name || h.project?.title || t("No project");
      const prev = map.get(id) || { id, name, hours: 0 };
      prev.hours += parseFloat(h.time) || 0;
      map.set(id, prev);
    });
    return Array.from(map.values())
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 6);
  }, [hoursData, t]);

  const dayHours = useMemo(() => {
    const d = parseISO(selectedKey);
    return (hoursData || [])
      .filter((h) => isSameDay(parseISO((h.date || "").substring(0, 10)), d))
      .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  }, [hoursData, selectedKey]);

  const dayTotal = sumHoursList(dayHours);
  const dayNormal = scaledWeekdays
    ? normalHoursBetween(selectedKey, selectedKey, scaledWeekdays)
    : 7.5;

  const timelineBlocks = useMemo(() => {
    return dayHours
      .map((h) => {
        let fromH = null;
        let toH = null;
        if (h.start_time) {
          const [hh, mm] = String(h.start_time).split(":");
          fromH = parseInt(hh, 10) + parseInt(mm || 0, 10) / 60;
        }
        if (h.end_time) {
          const [hh, mm] = String(h.end_time).split(":");
          toH = parseInt(hh, 10) + parseInt(mm || 0, 10) / 60;
        } else if (fromH != null && h.time) {
          toH = fromH + (parseFloat(h.time) || 0);
        }
        if (fromH == null || toH == null) return null;
        const left = ((fromH - 6) / 18) * 100;
        const width = ((toH - fromH) / 18) * 100;
        if (left < -5 || left > 100) return null;
        return {
          id: h.id,
          left: Math.max(0, left),
          width: Math.max(3, Math.min(100 - left, width)),
          color: projectColor(h.project?.id || h.project_id),
          title: h.project?.name || h.description || "",
        };
      })
      .filter(Boolean);
  }, [dayHours]);

  // Week matrix rows
  const weekRows = useMemo(() => {
    if (controller.view !== "week") return [];
    const groupMap = new Map();
    (hoursData || []).forEach((hour) => {
      const projectName = hour.project?.name || hour.project?.title || t("No project");
      const paLine = projectActivityLine(hour.project_activity);
      const activityName = paLine || t("project_activity.missing");
      const paKey = hour.project_activity?.id ?? hour.project_activity_id ?? "none";
      const key = `${hour.project?.id || "none"}|${paKey}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          projectName,
          activityName,
          color: projectColor(hour.project?.id || hour.project_id),
          cells: weekDays.map(() => 0),
          total: 0,
        });
      }
      const g = groupMap.get(key);
      const dKey = format(parseISO((hour.date || "").substring(0, 10)), "yyyy-MM-dd");
      const idx = weekDays.findIndex((d) => format(d, "yyyy-MM-dd") === dKey);
      const v = parseFloat(hour.time) || 0;
      if (idx >= 0) g.cells[idx] += v;
      g.total += v;
    });
    return Array.from(groupMap.values());
  }, [controller.view, hoursData, weekDays, t]);

  const weekColTotals = weekDays.map((_, i) =>
    weekRows.reduce((s, r) => s + (r.cells[i] || 0), 0)
  );
  const weekGrand = weekColTotals.reduce((a, b) => a + b, 0);

  // Period grouped by day
  const periodDays = useMemo(() => {
    if (controller.view !== "period" || !controller.date_from || !controller.date_to) return [];
    const start = parseISO(controller.date_from.substring(0, 10));
    const end = parseISO(controller.date_to.substring(0, 10));
    const days = eachDayOfInterval({ start, end });
    return days
      .map((day) => {
        const key = format(day, "yyyy-MM-dd");
        const rows = (hoursData || []).filter(
          (h) => (h.date || "").substring(0, 10) === key
        );
        if (!rows.length) return null;
        return {
          key,
          label: format(day, "EEEE d. MMMM", { locale: currentDateFnsLocale() }),
          total: sumHoursList(rows),
          rows,
        };
      })
      .filter(Boolean)
      .reverse();
  }, [controller.view, controller.date_from, controller.date_to, hoursData]);

  const periodBars = useMemo(() => {
    if (controller.view !== "period" || !controller.date_from || !controller.date_to) return [];
    const start = parseISO(controller.date_from.substring(0, 10));
    const end = parseISO(controller.date_to.substring(0, 10));
    const days = eachDayOfInterval({ start, end });
    const max = 8;
    return days.map((day) => {
      const key = format(day, "yyyy-MM-dd");
      const h = hoursInDateRange(hoursData, key, key);
      return {
        key,
        h,
        title: `${format(day, "dd.MM")} · ${h.toFixed(1)}t`,
        pct: Math.max((h / max) * 100, 4),
      };
    });
  }, [controller.view, controller.date_from, controller.date_to, hoursData]);

  const showStrip = controller.view !== "period";

  const periodDayCount = useMemo(() => {
    if (controller.view !== "period" || !controller.date_from || !controller.date_to) return 0;
    try {
      const start = parseISO(controller.date_from.substring(0, 10));
      const end = parseISO(controller.date_to.substring(0, 10));
      return Math.max(0, differenceInCalendarDays(end, start) + 1);
    } catch {
      return 0;
    }
  }, [controller.view, controller.date_from, controller.date_to]);

  const activePeriodPreset = useMemo(() => {
    if (controller.view !== "period" || !controller.date_from || !controller.date_to) return null;
    const from = controller.date_from.substring(0, 10);
    const to = controller.date_to.substring(0, 10);
    const now = new Date();
    const prev = subMonths(now, 1);
    const ranges = {
      week: [
        format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
        format(endOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd"),
      ],
      month: [
        format(startOfMonth(now), "yyyy-MM-dd"),
        format(endOfMonth(now), "yyyy-MM-dd"),
      ],
      last_month: [
        format(startOfMonth(prev), "yyyy-MM-dd"),
        format(endOfMonth(prev), "yyyy-MM-dd"),
      ],
      quarter: [
        format(startOfQuarter(now), "yyyy-MM-dd"),
        format(endOfQuarter(now), "yyyy-MM-dd"),
      ],
    };
    return (
      ["week", "month", "last_month", "quarter"].find(
        (key) => ranges[key][0] === from && ranges[key][1] === to
      ) ?? null
    );
  }, [controller.view, controller.date_from, controller.date_to]);

  const applyPeriodPreset = (presetKey) => {
    const now = new Date();
    let type = "month";
    let anchor = now;
    if (presetKey === "week") {
      type = "week";
    } else if (presetKey === "month") {
      type = "month";
    } else if (presetKey === "last_month") {
      type = "month";
      anchor = subMonths(now, 1);
    } else if (presetKey === "quarter") {
      type = "quarter";
    }
    let from;
    let to;
    if (type === "week") {
      from = startOfWeek(anchor, { weekStartsOn: 1 });
      to = endOfWeek(anchor, { weekStartsOn: 1 });
    } else if (type === "quarter") {
      from = startOfQuarter(anchor);
      to = endOfQuarter(anchor);
    } else {
      from = startOfMonth(anchor);
      to = endOfMonth(anchor);
    }
    setPeriodType(type);
    updateController({
      ...controller,
      view: "period",
      date: format(anchor, "yyyy-MM-dd"),
      date_from: format(from, "yyyy-MM-dd"),
      date_to: format(to, "yyyy-MM-dd"),
    });
  };

  const daySub = (h) => {
    if (!h) return t("hours.sheet.day_none");
    if (h >= 7.5) return t("hours.sheet.day_complete");
    return t("hours.sheet.day_partial");
  };

  return (
    <div className="hts">
      {/* Sync / week info banners — Tripletex/unsynced kun for admin (canProcessHours) */}
      <div className="hts-alerts">
        {canProcessHours && unsynced.length > 0 && (
          <div className="hts-alert hts-alert-warn">
            <div className="hts-alert-icon">!</div>
            <div style={{ minWidth: 0 }}>
              <div className="hts-alert-title">
                {t("hours.sheet.unsynced_title", { count: unsynced.length })}
              </div>
              <div className="hts-alert-sub">{t("hours.sheet.unsynced_body")}</div>
            </div>
            <div className="hts-alert-actions">
              <button
                type="button"
                className="hts-alert-btn hts-alert-btn-ghost"
                onClick={() => unsynced[0]?.id && onHourClick?.(unsynced[0].id)}
              >
                {t("hours.sheet.unsynced_see")}
              </button>
              <button
                type="button"
                className="hts-alert-btn hts-alert-btn-solid"
                onClick={() => {
                  setSelectedHour(unsynced[0]);
                  setProcessDialogOpen(true);
                }}
              >
                {t("hours.sheet.unsynced_process")}
              </button>
            </div>
          </div>
        )}
        {!infoBannerHidden && leftThisWeek != null && leftThisWeek > 0 && (
          <div className="hts-alert hts-alert-info">
            <div className="hts-alert-icon">i</div>
            <div style={{ minWidth: 0 }}>
              <div className="hts-alert-title">
                {t("hours.sheet.week_progress_title", { week: weekNum })}
              </div>
              <div className="hts-alert-sub">
                {t("hours.sheet.week_progress_body", {
                  hours: leftThisWeek.toFixed(1),
                })}
              </div>
            </div>
            <button
              type="button"
              className="hts-alert-dismiss"
              onClick={() => setInfoBannerHidden(true)}
            >
              {t("hours.sheet.hide_banner")}
            </button>
          </div>
        )}
      </div>

      {/* Week strip / period controls */}
      <section className="hts-strip-card">
        <div className="hts-strip-head">
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <span className="hts-strip-title">
              {controller.view === "period"
                ? t("Period")
                : t("hours.sheet.week_label", { week: weekNum })}
            </span>
            <span className="hts-strip-range">
              {controller.view === "period" && controller.date_from && controller.date_to
                ? `${format(parseISO(controller.date_from), "d. MMMM yyyy", { locale: currentDateFnsLocale() })} – ${format(parseISO(controller.date_to), "d. MMMM yyyy", { locale: currentDateFnsLocale() })}`
                : `${format(weekStart, "d.", { locale: currentDateFnsLocale() })}–${format(weekEnd, "d. MMMM yyyy", { locale: currentDateFnsLocale() })}`}
            </span>
          </div>
          <div className="hts-strip-nav">
            <button
              type="button"
              className="hts-icon-btn"
              onClick={() => navigateDate("prev")}
              aria-label={t("hours.quick.prev_week")}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button type="button" className="hts-today-btn" onClick={goToToday}>
              {t("Today")}
            </button>
            <button
              type="button"
              className="hts-icon-btn"
              onClick={() => navigateDate("next")}
              aria-label={t("hours.quick.next_week")}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
            {showFilters && (
              <>
                <div className="hts-nav-divider" />
                <button
                  type="button"
                  className="hts-icon-btn hts-filter-btn"
                  onClick={() => setShowFiltersMenu((v) => !v)}
                  title={t("Filters")}
                  aria-label={t("Filters")}
                >
                  <FunnelIcon className="h-4 w-4" />
                  {activeFiltersCount > 0 && (
                    <span className="hts-filter-badge">{activeFiltersCount}</span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {controller.view === "period" && controller.date_from && controller.date_to && (
          <div className="hts-period-row">
            <div className="hts-period-range">
              <span className="hts-period-label">{t("number.from")}</span>
              <span className="hts-period-value">
                {format(parseISO(controller.date_from), "dd.MM.yyyy")}
              </span>
              <span style={{ color: "#C9C6BE" }}>→</span>
              <span className="hts-period-label">{t("number.to")}</span>
              <span className="hts-period-value">
                {format(parseISO(controller.date_to), "dd.MM.yyyy")}
              </span>
            </div>
            {[
              { key: "week", label: t("hours.sheet.stats_this_week") },
              { key: "month", label: t("hours.sheet.preset_this_month") },
              { key: "last_month", label: t("hours.sheet.preset_last_month") },
              { key: "quarter", label: t("hours.sheet.preset_quarter") },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                className={`hts-preset${activePeriodPreset === p.key ? " is-active" : ""}`}
                onClick={() => applyPeriodPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
            <div className="hts-period-meta">
              {t("hours.sheet.period_meta", {
                days: periodDayCount,
                count: periodDays.length,
              })}
            </div>
          </div>
        )}

        {showStrip && (
          <div className="hts-strip-grid">
            {dayTotals.map(({ day, key, hours }) => {
              const sel = key === selectedKey;
              const pct = Math.max((hours / maxDay) * 100, hours ? 8 : 3);
              return (
                <button
                  key={key}
                  type="button"
                  className={`hts-day-card${sel ? " is-selected" : ""}`}
                  onClick={() => pickDay(day)}
                >
                  <div className="hts-day-top">
                    <span className="hts-day-dow">{format(day, "EEE", { locale: currentDateFnsLocale() })}</span>
                    <span className="hts-day-date">{format(day, "dd.MM")}</span>
                  </div>
                  <div className="hts-day-bar-wrap">
                    <div
                      className={`hts-day-bar ${hours ? "has-hours" : "no-hours"}`}
                      style={{ height: `${pct}%` }}
                    />
                  </div>
                  <div className="hts-day-hours">{hours.toFixed(1)}t</div>
                  <div className="hts-day-sub">{daySub(hours)}</div>
                </button>
              );
            })}
          </div>
        )}

        {showFiltersMenu && (
          <div className="hts-filters">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <div>
                <div className="hts-split-title" style={{ marginBottom: 4 }}>{t("Filters")}</div>
                <div style={{ fontSize: 13, color: "var(--hts-soft)" }}>
                  {t("Narrow down hours by project or activity")}
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <button type="button" className="hts-alert-dismiss" onClick={clearFilters}>
                  {t("Clear Filters")}
                </button>
              )}
            </div>
            <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  {t("Project Filter")}
                </label>
                <ProjectSelector
                  noLabel
                  name="project_filter"
                  selected={selectedProject}
                  onChange={(data) => {
                    const obj = data?.target?.object;
                    setSelectedProject(obj);
                    if (!obj?.id) setSelectedProjectActivity(null);
                  }}
                  placeholder={t("All Projects")}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  {t("project_activity.label")}
                </label>
                <ModernProjectActivitySelectorField
                  name="project_activity_filter_id"
                  label={false}
                  selected={selectedProjectActivity}
                  project={selectedProject?.id ? selectedProject : null}
                  setFieldValue={(field, val) => {
                    if (field === "project_activity") setSelectedProjectActivity(val);
                    if (field === "project_activity_filter_id" && (val == null || val === "")) {
                      setSelectedProjectActivity(null);
                    }
                  }}
                  errors={{}}
                />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Mobile compact stats */}
      <div className="hts-mobile-stats">
        <span style={{ color: "var(--hts-muted)", fontWeight: 600 }}>{t("Week")}:</span>
        <strong>{workedThisWeek.toFixed(1)}t</strong>
        <span style={{ color: "#C9C6BE" }}>·</span>
        <span style={{ color: "var(--hts-muted)", fontWeight: 600 }}>{t("hours.sheet.stats_compact_month")}:</span>
        <strong>{workedMonth.toFixed(1)}t</strong>
        <button type="button" onClick={() => setStatsDialogOpen(true)}>
          {t("hours.sheet.stats_show_more")}
        </button>
      </div>

      {isLoading ? (
        <div className="hts-loading">
          <div className="hts-loading-spinner" />
          <p style={{ marginTop: 12 }}>{t("Loading hours...")}</p>
        </div>
      ) : (
        <div className="hts-main-grid">
          {/* Main column */}
          <div>
            {controller.view === "day" && (
              <section className="hts-panel">
                <div className="hts-panel-head">
                  <div>
                    <h2>{format(parseISO(selectedKey), "EEEE, d. MMMM yyyy", { locale: currentDateFnsLocale() })}</h2>
                    <div className="hts-panel-sub">
                      {t("hours.sheet.entry_count", {
                        count: dayHours.length,
                        projects: new Set(dayHours.map((h) => h.project?.id).filter(Boolean)).size || 0,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="hts-panel-total">{dayTotal.toFixed(1)}t</div>
                    <div className="hts-panel-total-sub">
                      {t("hours.sheet.of_normal", { hours: (dayNormal ?? 7.5).toFixed(1) })}
                    </div>
                  </div>
                </div>

                <div className="hts-timeline-wrap">
                  <div className="hts-timeline">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="hts-timeline-tick"
                        style={{ left: `${(i / 6) * 100}%` }}
                      />
                    ))}
                    {timelineBlocks.map((b) => (
                      <div
                        key={b.id}
                        className="hts-timeline-block"
                        title={b.title}
                        style={{
                          left: `${b.left}%`,
                          width: `${b.width}%`,
                          background: b.color,
                        }}
                      />
                    ))}
                  </div>
                  <div className="hts-timeline-labels">
                    <span>06</span><span>09</span><span>12</span><span>15</span><span>18</span><span>21</span>
                  </div>
                </div>

                <div className="hts-entries">
                  {dayHours.length === 0 ? (
                    <div className="hts-empty">{t("No hours recorded for this day")}</div>
                  ) : (
                    dayHours.map((hour) => (
                      <EntryCard
                        key={hour.id}
                        hour={hour}
                        onHourClick={onHourClick}
                        canProcess={canProcessHours}
                        showSyncStatus={canProcessHours}
                        t={t}
                        onProcess={(h) => {
                          setSelectedHour(h);
                          setProcessDialogOpen(true);
                        }}
                      />
                    ))
                  )}
                  {onNewHourClick && (
                    <button
                      type="button"
                      className="hts-new-entry"
                      onClick={() => onNewHourClick(selectedKey)}
                    >
                      + {t("hours.sheet.new_entry_day")}
                    </button>
                  )}
                </div>
              </section>
            )}

            {controller.view === "week" && (
              <section className="hts-panel">
                <div className="hts-panel-head">
                  <div>
                    <h2>
                      {t("hours.sheet.week_label", { week: weekNum })} ·{" "}
                      {format(weekStart, "d.", { locale: currentDateFnsLocale() })}–{format(weekEnd, "d. MMMM", { locale: currentDateFnsLocale() })}
                    </h2>
                    <div className="hts-panel-sub">
                      {t("hours.sheet.week_matrix_sub", {
                        projects: projectSplit.length,
                        activities: weekRows.length,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="hts-panel-total">{weekGrand.toFixed(1)}t</div>
                    <div className="hts-panel-total-sub">
                      {normalThisWeek != null
                        ? t("hours.sheet.of_normal", { hours: normalThisWeek.toFixed(1) })
                        : t("hours.sheet.stats_no_schedule")}
                    </div>
                  </div>
                </div>
                <div className="hts-week-scroll">
                  <div className="hts-week-table">
                    <div className="hts-week-row head">
                      <div className="hts-week-col-label">{t("hours.period_table.project_and_line")}</div>
                      {weekDays.map((d) => {
                        const key = format(d, "yyyy-MM-dd");
                        return (
                          <div
                            key={key}
                            className={`hts-week-dow${key === selectedKey ? " is-active" : ""}`}
                          >
                            <span style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, opacity: 0.6 }}>
                              {format(d, "EEE", { locale: currentDateFnsLocale() })}
                            </span>
                            <span className="dm">{format(d, "dd.MM")}</span>
                          </div>
                        );
                      })}
                      <div className="hts-week-col-label" style={{ textAlign: "right" }}>
                        {t("Sum")}
                      </div>
                    </div>
                    {weekRows.length === 0 ? (
                      <div className="hts-empty">{t("No hours in this period")}</div>
                    ) : (
                      weekRows.map((r, idx) => (
                        <div key={idx} className="hts-week-row">
                          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                            <span className="hts-proj-dot" style={{ background: r.color }} />
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {r.projectName}
                              </span>
                              <span style={{ display: "block", fontSize: 11.5, color: "var(--hts-muted)", marginTop: 2 }}>
                                {r.activityName}
                              </span>
                            </span>
                          </div>
                          {r.cells.map((v, i) => (
                            <div key={i} className={`hts-week-cell ${v ? "has" : "empty"}`}>
                              {v ? v.toFixed(1) : "–"}
                            </div>
                          ))}
                          <div className="hts-week-sum">{r.total.toFixed(1)}</div>
                        </div>
                      ))
                    )}
                    {weekRows.length > 0 && (
                      <div className="hts-week-row foot">
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--hts-body)" }}>
                          {t("hours.sheet.sum_day")}
                        </div>
                        {weekColTotals.map((v, i) => (
                          <div key={i} className={`hts-week-cell ${v ? "has" : "empty"}`} style={{ fontSize: 14, fontWeight: 600 }}>
                            {v ? v.toFixed(1) : "–"}
                          </div>
                        ))}
                        <div className="hts-week-sum" style={{ fontSize: 17 }}>{weekGrand.toFixed(1)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {controller.view === "period" && (
              <section className="hts-panel">
                <div className="hts-panel-head">
                  <div>
                    <h2>
                      {format(parseISO(controller.date_from), "d.", { locale: currentDateFnsLocale() })} –{" "}
                      {format(parseISO(controller.date_to), "d. MMMM yyyy", { locale: currentDateFnsLocale() })}
                    </h2>
                    <div className="hts-panel-sub">
                      {t("hours.sheet.period_sub", {
                        entries: (hoursData || []).length,
                        days: periodDays.length,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="hts-panel-total">{workedPeriod.toFixed(1)}t</div>
                    <div className="hts-panel-total-sub">
                      {periodDays.length
                        ? t("hours.sheet.period_avg", {
                            hours: (workedPeriod / periodDays.length).toFixed(1),
                          })
                        : "—"}
                    </div>
                  </div>
                </div>
                <div style={{ padding: "18px 24px 4px" }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 76 }}>
                    {periodBars.map((b) => (
                      <div
                        key={b.key}
                        title={b.title}
                        style={{ flex: 1, height: "100%", display: "flex", alignItems: "flex-end" }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: `${b.pct}%`,
                            borderRadius: 6,
                            background: b.h ? "var(--hts-accent)" : "#EDEAE3",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {periodBars.length > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontFamily: "var(--hts-mono)", fontSize: 10.5, color: "#A6A29A" }}>
                      <span>{format(parseISO(controller.date_from), "dd.MM")}</span>
                      <span>{format(parseISO(controller.date_to), "dd.MM")}</span>
                    </div>
                  )}
                </div>
                <div className="hts-entries" style={{ gap: 18 }}>
                  {periodDays.length === 0 ? (
                    <div className="hts-empty">{t("No hours in this period")}</div>
                  ) : (
                    periodDays.map((d) => (
                      <div key={d.key}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 6px 10px" }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{d.label}</span>
                          <span style={{ height: 1, flex: 1, background: "var(--hts-border-soft)" }} />
                          <span style={{ fontFamily: "var(--hts-mono)", fontSize: 13, fontWeight: 600, color: "var(--hts-body)" }}>
                            {d.total.toFixed(1)}t
                          </span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {d.rows.map((hour) => (
                            <EntryCard
                              key={hour.id}
                              hour={hour}
                              onHourClick={onHourClick}
                              canProcess={canProcessHours}
                              showSyncStatus={canProcessHours}
                              t={t}
                              onProcess={(h) => {
                                setSelectedHour(h);
                                setProcessDialogOpen(true);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="hts-side">
            <section className="hts-week-hero">
              <div className="hts-week-hero-top">
                <span className="hts-week-hero-label">{t("hours.sheet.stats_this_week")}</span>
                {pctThisWeek != null && (
                  <span className="hts-week-hero-pct">{Math.round(pctThisWeek)} %</span>
                )}
              </div>
              <div className="hts-week-hero-num">
                <span className="big">{workedThisWeek.toFixed(1)}</span>
                <span className="unit">t</span>
                <span className="of">
                  {normalThisWeek != null
                    ? t("hours.sheet.of_normal_short", { hours: normalThisWeek.toFixed(1) })
                    : ""}
                </span>
              </div>
              <div className="hts-week-hero-bar">
                <div
                  style={{
                    width: `${Math.min(100, pctThisWeek != null ? pctThisWeek : 0)}%`,
                  }}
                />
              </div>
              <div className="hts-week-hero-foot">
                <span>
                  {leftThisWeek != null
                    ? t("hours.sheet.hours_left", { hours: leftThisWeek.toFixed(1) })
                    : "—"}
                </span>
                <span>
                  {t("hours.sheet.prev_week_short", { hours: workedPrevWeek.toFixed(1) })}
                </span>
              </div>
            </section>

            <section className="hts-stat-list">
              <div className="hts-stat-row">
                <div>
                  <div className="hts-stat-label">{t("hours.sheet.stats_selected_period")}</div>
                  <div className="hts-stat-sub">{t("hours.sheet.stats_list_hint")}</div>
                </div>
                <div className="hts-stat-value">{workedPeriod.toFixed(1)}t</div>
              </div>
              <div className="hts-stat-row">
                <div>
                  <div className="hts-stat-label">{t("hours.sheet.stats_calendar_month")}</div>
                  <div className="hts-stat-sub">
                    {statsLoading
                      ? t("hours.sheet.stats_loading_extra")
                      : format(weekAnchor, "MMMM yyyy", { locale: currentDateFnsLocale() })}
                  </div>
                </div>
                <div className="hts-stat-value">{workedMonth.toFixed(1)}t</div>
              </div>
              <div className="hts-stat-row">
                <div>
                  <div className="hts-stat-label">{t("hours.sheet.stats_prev_week")}</div>
                  <div className="hts-stat-sub">{t("hours.sheet.stats_weeks_heading")}</div>
                </div>
                <div className="hts-stat-value">{workedPrevWeek.toFixed(1)}t</div>
              </div>
            </section>

            {projectSplit.length > 0 && (
              <section className="hts-split">
                <div className="hts-split-title">{t("hours.sheet.project_split")}</div>
                <div className="hts-split-bar">
                  {projectSplit.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        flex: Math.max(p.hours, 0.2),
                        background: projectColor(p.id === "none" ? 0 : p.id),
                      }}
                    />
                  ))}
                </div>
                <div>
                  {projectSplit.map((p) => (
                    <div key={p.id} className="hts-split-row">
                      <span
                        className="hts-proj-dot"
                        style={{ background: projectColor(p.id === "none" ? 0 : p.id) }}
                      />
                      <span className="hts-split-name">{p.name}</span>
                      <span className="hts-split-hours">{p.hours.toFixed(1)}t</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {canProcessHours && (
        <ApproveHourDialog
          hour={selectedHour}
          open={processDialogOpen}
          onClose={() => {
            setProcessDialogOpen(false);
            setSelectedHour(null);
          }}
          onRefresh={handleHourUpdated}
        />
      )}

      <PMDialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        title={t("hours.sheet.stats_overview_title")}
        dense
      >
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="hts-stat-row" style={{ border: "none", padding: 0 }}>
            <div className="hts-stat-label">{t("hours.sheet.stats_this_week")}</div>
            <div className="hts-stat-value">{workedThisWeek.toFixed(1)}t</div>
          </div>
          <div className="hts-stat-row" style={{ border: "none", padding: 0 }}>
            <div className="hts-stat-label">{t("hours.sheet.stats_calendar_month")}</div>
            <div className="hts-stat-value">{workedMonth.toFixed(1)}t</div>
          </div>
          <div className="hts-stat-row" style={{ border: "none", padding: 0 }}>
            <div className="hts-stat-label">{t("hours.sheet.stats_prev_week")}</div>
            <div className="hts-stat-value">{workedPrevWeek.toFixed(1)}t</div>
          </div>
          <button
            type="button"
            className="hts-add-btn"
            style={{ justifyContent: "center", marginTop: 8 }}
            onClick={() => setStatsDialogOpen(false)}
          >
            {t("hours.sheet.stats_close")}
          </button>
        </div>
      </PMDialog>
    </div>
  );
}
